/**
 * FSD Part 9 — Human Capital Management. Sections follow the FSD's feature
 * specifications 9.2.1–9.2.7 (HCM-F01…HCM-F07) in order, then the part
 * catalogue 9.1 for service-charge distribution (HCM-FR-019), which has no
 * feature specification of its own. 9.2.6 has no section: no current screen
 * maps to it.
 *
 * Item ids are stable and must never change: favourites and recents are
 * keyed off them in localStorage. Section ids (`s-<fsd>`) are new in C17.
 *
 * Detail, create and tab routes (`/hr/employees/:id`, `/hr/exits/:id`,
 * `/payroll/runs/:id`, `/roster/rosters/:id`, `/payroll/returns/:id`, …) are
 * deliberately absent: they are reached from the list or document that owns
 * them. The sidebar lists destinations, not URLs.
 *
 * Labels point at each module's `routeTitles` rather than its `nav.*` strip:
 * route titles are already qualified, and a sidebar entry that cannot drift
 * from the page it opens is the point of §11.
 */
import type { NavGroup } from '../types'
import { HR_PERMISSIONS } from '@/modules/hr/permissions'
import { HR_C10_PERMISSIONS } from '@/modules/hr/permissionsC10'
import { HR_C11_PERMISSIONS } from '@/modules/hr/permissionsC11'
import { PAYROLL_PERMISSIONS } from '@/modules/payroll/permissions'
import { PAYROLL_C10_PERMISSIONS } from '@/modules/payroll/permissionsC10'
import { ROSTER_PERMISSIONS } from '@/modules/roster/permissions'

export const P09_HCM_GROUPS: NavGroup[] = [
  {
    id: 's-9.2.1',
    kind: 'feature',
    fsd: '9.2.1',
    code: 'HCM-F01',
    labelKey: 'nav.fsd.s9_2_1.short',
    fullTitleKey: 'nav.fsd.s9_2_1.full',
    items: [
      {
        id: 'hr-employees',
        labelKey: 'hr.routeTitles.employees',
        to: '/hr/employees',
        icon: 'users',
        permission: HR_PERMISSIONS.employeeRead,
        keywordsKey: 'nav.items.hrEmployees.keywords',
        descriptionKey: 'nav.items.hrEmployees.description',
      },
      {
        id: 'hr-work-permits',
        labelKey: 'hr.routeTitles.workPermits',
        to: '/hr/work-permits',
        icon: 'key',
        permission: HR_PERMISSIONS.employeeRead,
        keywordsKey: 'nav.items.hrWorkPermits.keywords',
        descriptionKey: 'nav.items.hrWorkPermits.description',
      },
      {
        id: 'hr-permit-quotas',
        labelKey: 'hr.routeTitles.permitQuotas',
        to: '/hr/permit-quotas',
        icon: 'key',
        permission: HR_C10_PERMISSIONS.permitRead,
      },
      {
        id: 'hr-exits',
        labelKey: 'hr.routeTitles.exits',
        to: '/hr/exits',
        icon: 'logout',
        permission: HR_C10_PERMISSIONS.exitRead,
      },
    ],
  },
  {
    // Recruitment. Three screens rather than one because a requisition is
    // approved headcount, a candidate is a person, and an offer is a document
    // — each with its own permission on the server.
    id: 's-9.2.2',
    kind: 'feature',
    fsd: '9.2.2',
    code: 'HCM-F02',
    labelKey: 'nav.fsd.s9_2_2.short',
    fullTitleKey: 'nav.fsd.s9_2_2.full',
    items: [
      {
        id: 'hr-requisitions',
        labelKey: 'hr.routeTitles.requisitions',
        to: '/hr/requisitions',
        icon: 'inbox',
        permission: HR_PERMISSIONS.requisitionRead,
        keywordsKey: 'nav.items.hrRequisitions.keywords',
        descriptionKey: 'nav.items.hrRequisitions.description',
      },
      {
        id: 'hr-candidates',
        labelKey: 'hr.routeTitles.candidates',
        to: '/hr/candidates',
        icon: 'user',
        permission: HR_PERMISSIONS.candidateRead,
        keywordsKey: 'nav.items.hrCandidates.keywords',
        descriptionKey: 'nav.items.hrCandidates.description',
      },
      {
        id: 'hr-offers',
        labelKey: 'hr.routeTitles.offers',
        to: '/hr/offers',
        icon: 'mail',
        permission: HR_PERMISSIONS.offerRead,
        keywordsKey: 'nav.items.hrOffers.keywords',
        descriptionKey: 'nav.items.hrOffers.description',
      },
    ],
  },
  {
    id: 's-9.2.3',
    kind: 'feature',
    fsd: '9.2.3',
    code: 'HCM-F03',
    labelKey: 'nav.fsd.s9_2_3.short',
    fullTitleKey: 'nav.fsd.s9_2_3.full',
    items: [
      {
        id: 'hr-attendance',
        labelKey: 'hr.routeTitles.attendance',
        to: '/hr/attendance',
        icon: 'check',
        permission: HR_PERMISSIONS.attendanceRead,
        keywordsKey: 'nav.items.hrAttendance.keywords',
        descriptionKey: 'nav.items.hrAttendance.description',
      },
      {
        id: 'hr-overtime-rules',
        labelKey: 'hr.routeTitles.overtimeRules',
        to: '/hr/overtime-rules',
        icon: 'history',
        permission: HR_PERMISSIONS.overtimeRuleRead,
        keywordsKey: 'nav.items.hrOvertimeRules.keywords',
        descriptionKey: 'nav.items.hrOvertimeRules.description',
      },
      {
        id: 'roster-rosters',
        labelKey: 'roster.routeTitles.rosters',
        to: '/roster/rosters',
        icon: 'calendar',
        permission: ROSTER_PERMISSIONS.rosterRead,
      },
      {
        id: 'roster-shift-definitions',
        labelKey: 'roster.routeTitles.shiftDefinitions',
        to: '/roster/shift-definitions',
        icon: 'clock',
        permission: ROSTER_PERMISSIONS.shiftPatternRead,
      },
      {
        id: 'roster-shift-patterns',
        labelKey: 'roster.routeTitles.shiftPatterns',
        to: '/roster/shift-patterns',
        icon: 'refresh',
        permission: ROSTER_PERMISSIONS.shiftPatternRead,
      },
      {
        id: 'roster-working-time-rules',
        labelKey: 'roster.routeTitles.workingTimeRules',
        to: '/roster/working-time-rules',
        icon: 'scale',
        permission: ROSTER_PERMISSIONS.workingTimeRuleRead,
      },
      {
        id: 'roster-demand',
        labelKey: 'roster.routeTitles.demand',
        to: '/roster/demand',
        icon: 'history',
        permission: ROSTER_PERMISSIONS.demandRead,
      },
      {
        id: 'roster-exceptions',
        labelKey: 'roster.routeTitles.exceptions',
        to: '/roster/exceptions',
        icon: 'alert',
        permission: ROSTER_PERMISSIONS.exceptionRead,
      },
      {
        id: 'roster-labour-cost',
        labelKey: 'roster.routeTitles.labourCost',
        to: '/roster/labour-cost',
        icon: 'sitemap',
        permission: ROSTER_PERMISSIONS.labourCostRead,
      },
    ],
  },
  {
    id: 's-9.2.4',
    kind: 'feature',
    fsd: '9.2.4',
    code: 'HCM-F04',
    labelKey: 'nav.fsd.s9_2_4.short',
    fullTitleKey: 'nav.fsd.s9_2_4.full',
    items: [
      {
        id: 'hr-leave-requests',
        labelKey: 'hr.routeTitles.leaveRequests',
        to: '/hr/leave/requests',
        icon: 'inbox',
        permission: HR_PERMISSIONS.leaveRead,
        keywordsKey: 'nav.items.hrLeaveRequests.keywords',
        descriptionKey: 'nav.items.hrLeaveRequests.description',
      },
      {
        id: 'hr-leave-calendar',
        labelKey: 'hr.routeTitles.leaveCalendar',
        to: '/hr/leave/calendar',
        icon: 'clock',
        permission: HR_PERMISSIONS.leaveRead,
        keywordsKey: 'nav.items.hrLeaveCalendar.keywords',
        descriptionKey: 'nav.items.hrLeaveCalendar.description',
      },
      {
        id: 'hr-leave-types',
        labelKey: 'hr.routeTitles.leaveTypes',
        to: '/hr/leave/types',
        icon: 'settings',
        permission: HR_PERMISSIONS.leaveTypeRead,
        keywordsKey: 'nav.items.hrLeaveTypes.keywords',
        descriptionKey: 'nav.items.hrLeaveTypes.description',
      },
      {
        id: 'hr-leave-blackouts',
        labelKey: 'hr.routeTitles.leaveBlackouts',
        to: '/hr/leave/blackouts',
        icon: 'ban',
        permission: HR_C10_PERMISSIONS.leaveBlackoutManage,
      },
    ],
  },
  {
    id: 's-9.2.5',
    kind: 'feature',
    fsd: '9.2.5',
    code: 'HCM-F05',
    labelKey: 'nav.fsd.s9_2_5.short',
    fullTitleKey: 'nav.fsd.s9_2_5.full',
    items: [
      {
        id: 'payroll-runs',
        labelKey: 'payroll.routeTitles.runs',
        to: '/payroll/runs',
        icon: 'refresh',
        permission: PAYROLL_PERMISSIONS.runRead,
        keywordsKey: 'nav.items.payrollRuns.keywords',
        descriptionKey: 'nav.items.payrollRuns.description',
      },
      {
        id: 'payroll-components',
        labelKey: 'payroll.routeTitles.components',
        to: '/payroll/components',
        icon: 'settings',
        permission: PAYROLL_PERMISSIONS.componentRead,
        keywordsKey: 'nav.items.payrollComponents.keywords',
        descriptionKey: 'nav.items.payrollComponents.description',
      },
      {
        id: 'payroll-pay-groups',
        labelKey: 'payroll.routeTitles.payGroups',
        to: '/payroll/pay-groups',
        icon: 'users',
        permission: HR_C10_PERMISSIONS.payGroupRead,
      },
      {
        id: 'payroll-wage-bases',
        labelKey: 'payroll.routeTitles.wageBases',
        to: '/payroll/wage-bases',
        icon: 'scale',
        permission: PAYROLL_C10_PERMISSIONS.wageBaseRead,
      },
      {
        id: 'payroll-statutory-rules',
        labelKey: 'payroll.routeTitles.statutoryRules',
        to: '/payroll/statutory-rules',
        icon: 'shield',
        permission: PAYROLL_C10_PERMISSIONS.statutoryRuleRead,
      },
      {
        id: 'payroll-statutory-mappings',
        labelKey: 'payroll.routeTitles.statutoryMappings',
        to: '/payroll/statutory-mappings',
        icon: 'link',
        permission: PAYROLL_C10_PERMISSIONS.statutoryMappingRead,
      },
      {
        id: 'payroll-retro',
        labelKey: 'payroll.routeTitles.retro',
        to: '/payroll/retro',
        icon: 'history',
        permission: PAYROLL_C10_PERMISSIONS.retroRead,
      },
      {
        id: 'payroll-returns',
        labelKey: 'payroll.routeTitles.returns',
        to: '/payroll/returns',
        icon: 'inbox',
        permission: PAYROLL_C10_PERMISSIONS.statutoryReturnRead,
      },
      {
        id: 'payroll-year-end',
        labelKey: 'payroll.routeTitles.yearEnd',
        to: '/payroll/year-end',
        icon: 'calendar',
        permission: PAYROLL_C10_PERMISSIONS.yearEndRead,
      },
    ],
  },
  {
    id: 's-9.2.7',
    kind: 'feature',
    fsd: '9.2.7',
    code: 'HCM-F07',
    labelKey: 'nav.fsd.s9_2_7.short',
    fullTitleKey: 'nav.fsd.s9_2_7.full',
    items: [
      // `hr.skill.read`, not `hr.skill.manage`: the compliance list of lapsing
      // certificates is what the planning side needs, and editing the
      // catalogue is a separate authority.
      {
        id: 'hr-skills',
        labelKey: 'hr.routeTitles.skills',
        to: '/hr/skills',
        icon: 'shieldCheck',
        permission: HR_C11_PERMISSIONS.skillRead,
      },
    ],
  },
  // Catalogue 9.1 — service-charge distribution is specified only as a
  // catalogue requirement.
  {
    id: 's-9.1',
    kind: 'catalogue',
    fsd: '9.1',
    labelKey: 'nav.fsd.s9_1.short',
    fullTitleKey: 'nav.fsd.s9_1.full',
    items: [
      {
        id: 'payroll-service-charge',
        labelKey: 'payroll.routeTitles.serviceCharge',
        to: '/payroll/service-charge',
        icon: 'star',
        permission: PAYROLL_C10_PERMISSIONS.svchargeRead,
        code: 'HCM-FR-019',
      },
      {
        id: 'payroll-service-charge-schemes',
        labelKey: 'payroll.routeTitles.serviceChargeSchemes',
        to: '/payroll/service-charge/schemes',
        icon: 'settings',
        permission: PAYROLL_C10_PERMISSIONS.svchargeRead,
        code: 'HCM-FR-019',
      },
    ],
  },
]
